import React from "react";
import { consolidationRequest } from "../api/consolidationApi.js";
import { sheetPayload } from "../utils/consolidationPresentation.js";

// Only confirmed data may reach this hook. Local edits hide (and cancel) its result.
export default function useConsolidationPreview(row, enabled, { resultsHidden = false, savedGeneration = 0, suspended = false } = {}) {
  const [attempt, retry] = React.useReducer((value) => value + 1, 0);
  const [result, setResult] = React.useState(null);
  const body = ["validated", "archived"].includes(row.status) ? null : {
    ...(row.id ? { sheet_id: row.id } : { source: {
      dispatch_date: row.dispatch_date, dispatch_period: row.dispatch_period,
      source_key: row.source_key, source_version: row.source_version, day_version: row.day_version,
    } }), values: sheetPayload(row),
  };
  const key = JSON.stringify(body);
  // A saved calculation finishes even if another sheet becomes busy or this panel closes.
  const requestEnabled = !suspended && (enabled || Boolean(savedGeneration && result?.generation !== savedGeneration));
  const manualExcluded = Boolean(row.manual_order_id && (row.execution_decisions?.some(d => d.order_id === row.manual_order_id && d.status === "not_delivered")
    || row.visits.some(v => v.orders.some(o => o.id === row.manual_order_id && o.execution_excluded))));
  const awaitingInput = body !== null && !manualExcluded
    && !["service_start", "service_end", "lunch_start", "lunch_end"].some((field) => body.values[field])
    && !body.values.visits.some((visit) => visit.arrival || visit.departure);
  React.useEffect(() => {
    if (!requestEnabled || resultsHidden || body === null || (awaitingInput && !savedGeneration)
      || (result?.key === key && result.attempt === attempt && result.generation === savedGeneration)) return;
    const controller = new AbortController();
    let active = true;
    const timer = setTimeout(async () => {
      try {
        const calculation = await consolidationRequest("/sheets/preview", {
          method: "POST", body: JSON.parse(key), signal: controller.signal,
        });
        // The first blocking reason must not hide a stop whose hours break the chronology.
        const reasons = calculation.partial ? [...new Set([calculation.calculation_error.message,
          ...(calculation.timing_errors || []).filter((issue) => issue.code === "chronology").map((issue) => issue.message)])] : [];
        if (active) setResult(calculation.partial
          ? { key, attempt, generation: savedGeneration, calculation: null, timing: calculation, error: `Répartition complète : ${reasons.join(" ")}` }
          : { key, attempt, generation: savedGeneration, calculation });
      } catch (error) {
        if (active) setResult({ key, attempt, generation: savedGeneration, error: error.message, retryable: !error.status || error.status >= 500 });
      }
    }, 500);
    return () => { active = false; clearTimeout(timer); controller.abort(); };
  }, [key, requestEnabled, resultsHidden, savedGeneration, attempt, awaitingInput, result]);
  if (resultsHidden) return { calculation: null, resultsHidden: true, retry };
  if (["validated", "archived"].includes(row.status)) return { calculation: row.validated_calculation, retry };
  if (awaitingInput && !savedGeneration) return { calculation: null, awaitingInput: true, retry };
  if (result?.key === key && result.attempt === attempt && result.generation === savedGeneration) return {
    ...result, retry, savedCalculationFailed: Boolean(savedGeneration && result.error && !result.timing),
  };
  if (savedGeneration && result?.generation !== savedGeneration) return { calculation: null, pending: true, retry };
  if (!enabled) return { calculation: null, deferred: true, retry };
  return { calculation: null, pending: true, retry };
}
